import { Box, Chip, Typography } from "@mui/material";
import { Cell, Pie, PieChart } from "recharts";
import { GAUGE_BAND_COLORS } from "../colors";
import { useDrillDownDialog } from "../DrillDownProvider";
import { gaugeBandsFromGoal } from "../gaugeBands";
import { DrillDownConfig, GaugeBands, WidgetExplanationContent } from "../types";
import { WidgetCard } from "./WidgetCard";

const GAUGE_WIDTH = 260;
const GAUGE_HEIGHT = 135;
const GAUGE_CENTER_X = 130;
const GAUGE_CENTER_Y = 120;
const GAUGE_INNER_RADIUS = 72;
const GAUGE_OUTER_RADIUS = 108;
const RADIAN = Math.PI / 180;

const createBandSegments = (bands: GaugeBands) => {
    const segments = [
        { value: bands.yellowFrom, color: GAUGE_BAND_COLORS.low },
        { value: bands.greenFrom - bands.yellowFrom, color: GAUGE_BAND_COLORS.middle },
        { value: bands.max - bands.greenFrom, color: GAUGE_BAND_COLORS.high },
    ];
    return segments;
};

const createNeedleElements = (value: number, bands: GaugeBands) => {
    const boundedValue = Math.min(Math.max(value, 0), bands.max);
    const angle = 180 * (1 - boundedValue / bands.max);
    const length = (GAUGE_INNER_RADIUS + GAUGE_OUTER_RADIUS) / 2;
    const sin = Math.sin(-RADIAN * angle);
    const cos = Math.cos(-RADIAN * angle);
    const baseRadius = 4;
    const baseLeftX = GAUGE_CENTER_X + baseRadius * sin;
    const baseLeftY = GAUGE_CENTER_Y - baseRadius * cos;
    const baseRightX = GAUGE_CENTER_X - baseRadius * sin;
    const baseRightY = GAUGE_CENTER_Y + baseRadius * cos;
    const tipX = GAUGE_CENTER_X + length * cos;
    const tipY = GAUGE_CENTER_Y + length * sin;

    const needleElements = [
        <circle key="needle-base" cx={GAUGE_CENTER_X} cy={GAUGE_CENTER_Y} r={baseRadius} fill="#666" />,
        <path key="needle-pointer" d={`M${baseLeftX} ${baseLeftY} L${baseRightX} ${baseRightY} L${tipX} ${tipY} Z`} fill="#666" />,
    ];
    return needleElements;
};

const createLimitLabels = (bands: GaugeBands) => {
    const midRadius = (GAUGE_INNER_RADIUS + GAUGE_OUTER_RADIUS) / 2;
    const boundaryAngle = 180 * (1 - bands.greenFrom / bands.max);
    const boundaryRadius = GAUGE_OUTER_RADIUS + 10;
    const boundaryX = GAUGE_CENTER_X + boundaryRadius * Math.cos(-RADIAN * boundaryAngle);
    const boundaryY = GAUGE_CENTER_Y + boundaryRadius * Math.sin(-RADIAN * boundaryAngle);

    const labelElements = [
        <text key="limit-min" x={GAUGE_CENTER_X - midRadius} y={GAUGE_CENTER_Y + 16} textAnchor="middle" fontSize={11} fill="#666">
            0
        </text>,
        <text key="limit-max" x={GAUGE_CENTER_X + midRadius} y={GAUGE_CENTER_Y + 16} textAnchor="middle" fontSize={11} fill="#666">
            {bands.max}
        </text>,
        <text key="limit-boundary" x={boundaryX} y={boundaryY} textAnchor="middle" fontSize={11} fill="#666">
            {bands.greenFrom}
        </text>,
    ];
    return labelElements;
};

type GaugeCardProps = {
    title: string;
    value: number | undefined;
    goal: number;
    explanation: WidgetExplanationContent;
    isLoading?: boolean;
    isError?: boolean;
    drillDown?: DrillDownConfig;
    chips?: Array<string>;
};

export const GaugeCard = ({ title, value, goal, explanation, isLoading, isError, drillDown, chips }: GaugeCardProps) => {
    const bands = gaugeBandsFromGoal(goal);
    const segments = createBandSegments(bands);
    const drillDownDialog = useDrillDownDialog();
    const isClickable = Boolean(drillDown && drillDownDialog && !isLoading);
    const hasChips = Boolean(!isLoading && chips && chips.length > 0);

    return (
        <WidgetCard title={title} explanation={explanation} bodyHeight={GAUGE_HEIGHT + (hasChips ? 66 : 40)} isLoading={isLoading} isError={isError}>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                onClick={isClickable ? () => drillDownDialog!.openDrillDown(drillDown!) : undefined}
                sx={isClickable ? { cursor: "pointer", borderRadius: 2, "&:hover": { backgroundColor: "#f7f8fa" } } : undefined}
            >
                <PieChart width={GAUGE_WIDTH} height={GAUGE_HEIGHT + 10}>
                    <Pie
                        data={segments}
                        dataKey="value"
                        cx={GAUGE_CENTER_X - 5}
                        cy={GAUGE_CENTER_Y - 5}
                        startAngle={180}
                        endAngle={0}
                        innerRadius={GAUGE_INNER_RADIUS}
                        outerRadius={GAUGE_OUTER_RADIUS}
                        stroke="none"
                        isAnimationActive={false}
                    >
                        {segments.map((segment, index) => (
                            <Cell key={index} fill={segment.color} />
                        ))}
                    </Pie>
                    {value !== undefined && createNeedleElements(value, bands)}
                    {createLimitLabels(bands)}
                </PieChart>
                <Box mt={-5}>
                    <Typography variant="h3" fontWeight={700} color="text.primary">
                        {value ?? "—"}
                    </Typography>
                </Box>
                {hasChips && (
                    <Box display="flex" flexWrap="wrap" justifyContent="center" gap={0.5} mt={0.5}>
                        {chips!.map((chip) => (
                            <Chip key={chip} label={chip} size="small" sx={{ height: 22, fontSize: "0.72rem", fontWeight: 600, "& .MuiChip-label": { px: 0.9 } }} />
                        ))}
                    </Box>
                )}
            </Box>
        </WidgetCard>
    );
};
